import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import ReactDatePicker from "react-datepicker";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import organizationData from "../../../utils/organizationData.json";

import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import { Token } from "react-bootstrap-typeahead";
import {
  HolidayFormData,
  HolidayListTableProduct,
} from "../../../types/holidays/HolidaysTypes";
import NavigationBreadcrumbs from "../../../common-elements/breadcrumbs/NavigationBreadcrumbs";
import { validationSchema } from "./validationHoliday";

const AddHolidaysPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [holidayData, setHolidayData] = useState<HolidayListTableProduct[]>([]);

  const typeOptions = [
    { name: t("National"), value: "National" },
    { name: t("Religious"), value: "Religious" },
    { name: t("Cultural"), value: "Cultural" },
    { name: t("Public"), value: "Public" },
  ];

  const initialValues: HolidayFormData = {
    name: "",
    type: "",
    country: [],
    date: new Date(),
    years: [],
    fixed: false,
  };

  const handleSubmit = (values: HolidayFormData) => {
    const submittedData: HolidayListTableProduct = {
      name: values.name,
      type: values.type,
      country: values.country,
      date: values.date,
      years: values.years,
      fixed: values.fixed,
    };

    setHolidayData([...holidayData, submittedData]);
    navigate(`${process.env.PUBLIC_URL}/holidays/list`);
  };

  const handleClickCancel = () => {
    navigate(`${process.env.PUBLIC_URL}/masterdata/holidays`);
  };

  const countryOptions = organizationData.countries.map((country) => ({
    name: country.label,
    value: country.value,
    flag: country.flag,
  }));

  return (
    <>
      <NavigationBreadcrumbs
        mainTitle={t("AddHolidays")}
        parent={t("Master Data")}
        links={[
          { label: "Holidays", url: `${process.env.PUBLIC_URL}/masterdata/holidays` },
          { label: "Add Holidays", active: true },
        ]}
      />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <CardBody>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ setFieldValue, values }) => (
                    <Form className="theme-form">
                      <Row>
                        <Col>
                          <FormGroup>
                            <Label check>
                              {t("HolidayName")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Field
                              name="name"
                              type="text"
                              className="form-control"
                              placeholder={t("HolidayNamePlaceholder")}
                            />
                            <ErrorMessage
                              name="name"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label check>
                              {t("Type")} <span className="txt-danger">*</span>
                            </Label>
                            <Typeahead
                              id="type-typeahead"
                              labelKey="name"
                              options={typeOptions}
                              placeholder={t("SelectType")}
                              selected={typeOptions.filter(
                                (opt) => opt.value === values.type
                              )}
                              onChange={(selected) =>
                                setFieldValue(
                                  "type",
                                  (selected[0] as { value: string })?.value ||
                                    ""
                                )
                              }
                              multiple={false}
                            />
                            <ErrorMessage
                              name="type"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                          <FormGroup className="d-flex flex-column align-items-stretch">
                            <Label check>
                              {t("Date")} <span className="txt-danger">*</span>
                            </Label>
                            <ReactDatePicker
                              className="datepicker-here form-control"
                              selected={values.date}
                              onChange={(date: Date | null) =>
                                setFieldValue("date", date)
                              }
                            />
                            <ErrorMessage
                              name="date"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label check>
                              {t("Country")}{" "}
                              <span className="txt-danger">*</span>
                            </Label>
                            <Typeahead
                              id="country-typeahead"
                              labelKey="name"
                              options={countryOptions}
                              placeholder={t("SelectCountry")}
                              selected={countryOptions.filter((opt) =>
                                values.country.includes(opt.value)
                              )}
                              onChange={(selected) =>
                                setFieldValue(
                                  "country",
                                  selected.map(
                                    (opt) => (opt as { value: string }).value
                                  )
                                )
                              }
                              className="typeahead-lg"
                              multiple
                              renderMenuItemChildren={(option) => {
                                if (typeof option === "string")
                                  return <span>{option}</span>;
                                return (
                                  <div className="d-flex align-items-center">
                                    {option.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${option.flag}.svg`}
                                        alt={option.name}
                                        style={{
                                          width: 20,
                                          height: 15,
                                          marginRight: 8,
                                        }}
                                      />
                                    )}
                                    <span>{option.name}</span>
                                  </div>
                                );
                              }}
                              renderToken={(option, props) => {
                                if (typeof option === "string")
                                  return <Token {...props} option={option} />;
                                return (
                                  <Token {...props} option={option}>
                                    {option.flag && (
                                      <img
                                        src={`/assets/fonts/flag-icon/${option.flag}.svg`}
                                        alt={option.name}
                                        style={{
                                          width: 20,
                                          height: 15,
                                          marginRight: 4,
                                        }}
                                      />
                                    )}
                                    {option.name}
                                  </Token>
                                );
                              }}
                            />
                            <ErrorMessage
                              name="country"
                              component="span"
                              className="text-danger"
                            />
                          </FormGroup>
                          <FormGroup className="d-flex flex-column align-items-stretch">
                            <Label check>{t("IsthisaTentativeDate")}?</Label>
                            <Label className="switch">
                              <Input
                                type="checkbox"
                                checked={values.fixed}
                                onChange={() =>
                                  setFieldValue("fixed", !values.fixed)
                                }
                              />
                              <span className="switch-state"></span>
                            </Label>
                            <ErrorMessage
                              name="fixed"
                              component="span"
                              className="text-danger"
                            />
                            <div className="text-end">
                              <Button
                                onClick={handleClickCancel}
                                color="secondary"
                                className="me-2"
                              >
                                {t("Cancel")}
                              </Button>
                              <Button color="primary" type="submit">
                                {t("Save")}
                              </Button>
                            </div>
                          </FormGroup>
                        </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AddHolidaysPage;
